import React, { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../convex_generated/api'
import { useUI } from '../../contexts/UIContext'
import { useToast } from '../../contexts/ToastContext'
import { convex } from '../../lib/convex'
import SlateRenderer, { type BranchMarker } from '../reader/SlateRenderer'
import {
  XMarkIcon,
  BookmarkIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ListBulletIcon,
  MinusIcon,
  PlusIcon,
  TrashIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import { BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid'
import { asId } from '../../lib/ids'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Chapter {
  title: string
  charOffset: number
  domIndex: number
  level: number
}

interface ImmersiveReaderProps {
  branchId: string
  contentNodes: unknown[]
  chapters: Chapter[]
  bookTitle: string
  branchTitle: string
  onClose: () => void
  /** Map of node-index → branch markers for inline indicators */
  branchMarkers?: Map<number, BranchMarker[]>
}

// ---------------------------------------------------------------------------
// Branch navigation stack entry
// ---------------------------------------------------------------------------

interface BranchStackEntry {
  branchId: string
  branchTitle: string
  page: number
  contentNodes: unknown[]
  branchMarkers?: Map<number, BranchMarker[]>
}

// ---------------------------------------------------------------------------
// Helper: extract text from Slate node
// ---------------------------------------------------------------------------

function getNodeText(node: unknown): string {
  if (!node || typeof node !== 'object') return ''
  const n = node as Record<string, unknown>
  if (typeof n.text === 'string') return n.text
  if (Array.isArray(n.children)) return (n.children as unknown[]).map(getNodeText).join('')
  return ''
}

/** Build a Map<nodeIndex, BranchMarker[]> by matching forkContext against content nodes. */
function computeBranchMarkersFromChildren(
  contentNodes: unknown[],
  children: Array<{ _id: string; title: string; category: string; wordCount: number; rating: number; forkContext?: string | null }>,
): Map<number, BranchMarker[]> {
  const map = new Map<number, BranchMarker[]>()
  const add = (idx: number, branch: BranchMarker) => {
    if (!map.has(idx)) map.set(idx, [])
    map.get(idx)!.push(branch)
  }
  const lastIdx = Math.max(0, contentNodes.length - 1)
  for (const child of children) {
    const marker: BranchMarker = { id: child._id, title: child.title, category: child.category, wordCount: child.wordCount, rating: child.rating }
    const ctx = child.forkContext?.trim()
    if (!ctx) { add(lastIdx, marker); continue }
    const needle = ctx.toLowerCase().slice(0, 50)
    let found = false
    for (let i = 0; i < contentNodes.length; i++) {
      if (getNodeText(contentNodes[i]).toLowerCase().includes(needle)) { add(i, marker); found = true; break }
    }
    if (!found) add(lastIdx, marker)
  }
  return map
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Map reading font size tokens to px values
const FONT_PX: Record<string, number> = { sm: 14, base: 16, lg: 18, xl: 22 }

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ImmersiveReader: React.FC<ImmersiveReaderProps> = ({
  branchId: initialBranchId,
  contentNodes: initialContentNodes,
  chapters,
  bookTitle,
  branchTitle: initialBranchTitle,
  onClose,
  branchMarkers: initialBranchMarkers,
}) => {
  const { readingFontSize, setReadingFontSize, readingFontFamily, setReadingFontFamily, theme, setTheme } = useUI()
  const { showToast } = useToast()

  // ── Branch navigation stack ──
  const [branchStack, setBranchStack] = useState<BranchStackEntry[]>([])
  const [activeBranchId, setActiveBranchId] = useState(initialBranchId)
  const [activeContentNodes, setActiveContentNodes] = useState(initialContentNodes)
  const [activeBranchTitle, setActiveBranchTitle] = useState(initialBranchTitle)
  const [activeBranchMarkers, setActiveBranchMarkers] = useState(initialBranchMarkers)
  const [loadingBranch, setLoadingBranch] = useState(false)

  // The current page, readable from callbacks declared before the page state
  // (and without them going stale between renders).
  const pageRef = useRef(0)

  const handleBranchClick = useCallback(async (targetBranchId: string) => {
    setLoadingBranch(true)
    try {
      // Push current state onto stack
      setBranchStack(prev => [...prev, {
        branchId: activeBranchId,
        branchTitle: activeBranchTitle,
        page: pageRef.current,
        contentNodes: activeContentNodes,
        branchMarkers: activeBranchMarkers,
      }])

      // Fetch the target branch data
      const branch = await convex.query(api.branches.getBranch, { branchId: asId<"branches">(targetBranchId) })
      if (!branch) { showToast({ message: 'Branch not found', type: 'error' }); setBranchStack(prev => prev.slice(0, -1)); return }

      // Load content
      let nodes: unknown[] = []
      if (branch.contentUrl) {
        try { nodes = await fetch(branch.contentUrl).then(r => r.json()) } catch { /* empty */ }
      } else if (Array.isArray(branch.content) && branch.content.length > 0) {
        nodes = branch.content
      }

      // Fetch child branches for markers
      const siblings = await convex.query(api.branches.getBranchesForBook, { bookId: branch.bookId })
      const children = siblings.filter((b) => b.parentBranchId === targetBranchId)
      const markers = children.length > 0 ? computeBranchMarkersFromChildren(nodes, children) : undefined

      setActiveBranchId(targetBranchId)
      setActiveBranchTitle(branch.title)
      setActiveContentNodes(nodes)
      setActiveBranchMarkers(markers)
      setPage(0)
    } catch {
      showToast({ message: 'Failed to load branch', type: 'error' })
      setBranchStack(prev => prev.slice(0, -1))
    } finally {
      setLoadingBranch(false)
    }
  }, [activeBranchId, activeBranchTitle, activeContentNodes, activeBranchMarkers, showToast])

  const handleReturnToPrevious = useCallback(() => {
    if (branchStack.length === 0) return
    const prev = branchStack[branchStack.length - 1]
    setBranchStack(s => s.slice(0, -1))
    setActiveBranchId(prev.branchId)
    setActiveBranchTitle(prev.branchTitle)
    setActiveContentNodes(prev.contentNodes)
    setActiveBranchMarkers(prev.branchMarkers)
    setPage(prev.page)
  }, [branchStack])

  const fontPx = FONT_PX[readingFontSize] ?? 16

  // ── Visible viewport height (accounts for mobile browser chrome) ──
  const [visibleH, setVisibleH] = useState(() =>
    window.visualViewport?.height ?? window.innerHeight,
  )
  // Container width for measurement
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerW, setContainerW] = useState(375)

  useEffect(() => {
    // Track visual viewport resizes (browser chrome show/hide, orientation)
    const vv = window.visualViewport
    const updateH = () => setVisibleH(vv?.height ?? window.innerHeight)
    if (vv) {
      vv.addEventListener('resize', updateH)
      vv.addEventListener('scroll', updateH)
    }
    window.addEventListener('resize', updateH)
    updateH()
    return () => {
      if (vv) {
        vv.removeEventListener('resize', updateH)
        vv.removeEventListener('scroll', updateH)
      }
      window.removeEventListener('resize', updateH)
    }
  }, [])

  // Track container width via ResizeObserver
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width
      if (w && w > 100) setContainerW(w)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // ── CSS column-based pagination ──
  // We render ALL content inside a multi-column container. Each column = one page.
  // The browser handles text-level breaking (even mid-paragraph), so no content
  // is ever lost between pages.
  const columnRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [totalPages, setTotalPages] = useState(1)

  // Padding inside the column container: py-4 = 16px top + 16px bottom
  const PAGE_PAD_Y = 16
  const COLUMN_GAP = 40  // px gap between columns (prevents sub-pixel bleed)
  const pageH = visibleH
  const usableH = pageH - PAGE_PAD_Y * 2  // content height per column
  const stride = containerW + COLUMN_GAP   // horizontal distance per page

  // Phase 1: Measure content height off-screen (single column) → generous column container width
  const [colContainerW, setColContainerW] = useState(containerW * 3)
  useLayoutEffect(() => {
    const el = measureRef.current
    if (!el || activeContentNodes.length === 0 || usableH <= 0) return
    const raf = requestAnimationFrame(() => {
      const h = el.scrollHeight
      const est = Math.max(2, Math.ceil(h / usableH) + 3) // +3 safety margin
      setColContainerW(est * stride)
    })
    return () => cancelAnimationFrame(raf)
  }, [activeContentNodes, readingFontSize, readingFontFamily, containerW, usableH, stride])

  // Phase 2: After column layout renders, measure sentinel position → exact page count
  useLayoutEffect(() => {
    const col = columnRef.current
    const sentinel = sentinelRef.current
    if (!col || !sentinel || activeContentNodes.length === 0) return
    const raf = requestAnimationFrame(() => {
      const colRect = col.getBoundingClientRect()
      const sRect = sentinel.getBoundingClientRect()
      // Visual distance from container left to sentinel right (transform-independent)
      const dist = sRect.right - colRect.left
      setTotalPages(Math.max(1, Math.ceil(dist / stride)))
    })
    return () => cancelAnimationFrame(raf)
  }, [colContainerW, stride, activeContentNodes, readingFontSize, readingFontFamily, containerW, visibleH])

  // Clamp current page when totalPages changes (e.g. orientation change, font size)
  useEffect(() => {
    setPage(p => (totalPages > 0 && p >= totalPages) ? totalPages - 1 : p)
  }, [totalPages])

  // ── Reading progress from backend ──
  const savedProgress = useQuery(api.reading.getProgress, { branchId: asId<"branches">(activeBranchId) })
  const saveProgress = useMutation(api.reading.saveProgress)
  const [page, setPage] = useState(0)
  useEffect(() => { pageRef.current = page }, [page])
  const [initialised, setInitialised] = useState(false)

  // Restore page from backend on mount
  useEffect(() => {
    if (initialised || savedProgress === undefined) return
    if (savedProgress && savedProgress.position >= 0 && savedProgress.position < totalPages) {
      setPage(savedProgress.position)
    }
    setInitialised(true)
  }, [savedProgress, totalPages, initialised])

  // Save progress when page changes (debounced)
  const saveTimeout = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => {
    if (!initialised || totalPages === 0) return
    clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(() => {
      saveProgress({ branchId: asId<"branches">(activeBranchId), position: page, totalLength: totalPages }).catch(() => {})
    }, 1500)
    return () => clearTimeout(saveTimeout.current)
  }, [page, totalPages, activeBranchId, saveProgress, initialised])

  // ── Bookmarks ──
  const bookmarks = useQuery(api.reading.getBookmarks, { branchId: asId<"branches">(activeBranchId) }) ?? []
  const addBookmark = useMutation(api.reading.addBookmark)
  const deleteBookmark = useMutation(api.reading.deleteBookmark)

  const isCurrentPageBookmarked = bookmarks.some((b: { position: number }) => b.position === page)
  const currentBookmarkId = bookmarks.find((b: { position: number }) => b.position === page)?._id

  const toggleBookmark = useCallback(async () => {
    if (isCurrentPageBookmarked && currentBookmarkId) {
      await deleteBookmark({ bookmarkId: currentBookmarkId })
      showToast({ message: 'Bookmark removed', type: 'info' })
    } else {
      await addBookmark({ branchId: asId<"branches">(activeBranchId), position: page })
      showToast({ message: `Page ${page + 1} bookmarked`, type: 'success' })
    }
  }, [isCurrentPageBookmarked, currentBookmarkId, deleteBookmark, addBookmark, activeBranchId, page, showToast])

  // ── Panel states ──
  const [showToc, setShowToc] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  const closeAllPanels = useCallback(() => {
    setShowToc(false)
    setShowSettings(false)
  }, [])

  // ── Navigation ──
  const goNext = useCallback(() => { closeAllPanels(); setPage(p => Math.min(p + 1, totalPages - 1)) }, [totalPages, closeAllPanels])
  const goPrev = useCallback(() => { closeAllPanels(); setPage(p => Math.max(p - 1, 0)) }, [closeAllPanels])
  const goToPage = useCallback((n: number) => { closeAllPanels(); setPage(Math.max(0, Math.min(n, totalPages - 1))) }, [totalPages, closeAllPanels])

  // Find which page a chapter lands on by checking heading position in the column layout
  const [chapterPageMap, setChapterPageMap] = useState<{ chapter: Chapter; pageIdx: number }[]>([])
  useLayoutEffect(() => {
    const col = columnRef.current
    if (!col || chapters.length === 0 || totalPages <= 1) {
      setChapterPageMap([])
      return
    }
    const raf = requestAnimationFrame(() => {
      const colRect = col.getBoundingClientRect()
      const headings = col.querySelectorAll('h1, h2, h3, h4, h5, h6')
      const map: { chapter: Chapter; pageIdx: number }[] = []
      headings.forEach((heading) => {
        const title = heading.textContent?.trim() ?? ''
        const chapter = chapters.find(ch => ch.title === title)
        if (chapter) {
          // Visual distance from column container left to heading left
          const dist = heading.getBoundingClientRect().left - colRect.left
          const pageIdx = Math.max(0, Math.min(Math.floor(dist / stride), totalPages - 1))
          map.push({ chapter, pageIdx })
        }
      })
      setChapterPageMap(map)
    })
    return () => cancelAnimationFrame(raf)
  }, [totalPages, chapters, stride])

  // ── Keyboard navigation ──
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault(); goNext()
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault(); goPrev()
      } else if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [goNext, goPrev, onClose])

  // ── Touch / swipe ──
  const touchStart = useRef<{ x: number; y: number; t: number } | null>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0]
    touchStart.current = { x: t.clientX, y: t.clientY, t: Date.now() }
  }, [])

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart.current) return
    const t = e.changedTouches[0]
    const dx = t.clientX - touchStart.current.x
    const dy = t.clientY - touchStart.current.y
    const dt = Date.now() - touchStart.current.t
    touchStart.current = null

    // Require a minimum 40px distance, and more horizontal than vertical, within 600ms
    const absDx = Math.abs(dx)
    const absDy = Math.abs(dy)

    if (dt < 600) {
      if (absDx > 40 && absDx > absDy) {
        // Horizontal swipe
        dx < 0 ? goNext() : goPrev()
      } else if (absDy > 40 && absDy > absDx) {
        // Vertical swipe: down = prev, up = next
        dy < 0 ? goNext() : goPrev()
      }
    }
  }, [goNext, goPrev])

  // ── Page turn animation ──
  const [turning, setTurning] = useState<'next' | 'prev' | null>(null)
  const prevPage = useRef(page)

  useEffect(() => {
    if (page !== prevPage.current) {
      setTurning(page > prevPage.current ? 'next' : 'prev')
      prevPage.current = page
      const id = setTimeout(() => setTurning(null), 500)
      return () => clearTimeout(id)
    }
  }, [page])

  // ── Tap zones (left 25% = prev, right 25% = next) ──
  const handleTap = useCallback((e: React.MouseEvent) => {
    // Don't navigate on button/link clicks
    if ((e.target as HTMLElement).closest('button, a')) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX - rect.left
    const pct = x / rect.width

    if (pct < 0.25) goPrev()
    else if (pct > 0.75) goNext()
  }, [goNext, goPrev])

  // ── UI visibility (tap center to toggle) ──
  const [showControls, setShowControls] = useState(true)
  const controlTimeout = useRef<ReturnType<typeof setTimeout>>()

  const autoHideControls = useCallback(() => {
    clearTimeout(controlTimeout.current)
    controlTimeout.current = setTimeout(() => setShowControls(false), 5000)
  }, [])

  useEffect(() => {
    autoHideControls()
    return () => clearTimeout(controlTimeout.current)
  }, [page, autoHideControls])

  const handleCenterTap = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a')) return
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const x = e.clientX - rect.left
    const pct = x / rect.width
    if (pct >= 0.25 && pct <= 0.75) {
      setShowControls(s => !s)
    }
  }, [])

  // Font size helpers
  type FontSize = 'sm' | 'base' | 'lg' | 'xl'
  const FONT_SIZES: FontSize[] = ['sm', 'base', 'lg', 'xl']
  const fontSizeIdx = FONT_SIZES.indexOf(readingFontSize)

  const fontFamilyCSS = readingFontFamily === 'serif'
    ? 'Georgia, "Times New Roman", serif'
    : readingFontFamily === 'mono'
      ? '"Fira Code", "JetBrains Mono", monospace'
      : 'system-ui, -apple-system, sans-serif'
  const slotFontFamily = readingFontFamily === 'mono' ? 'serif' as const : readingFontFamily === 'sans' ? 'sans' as const : 'serif' as const

  return (
    <div
      className="fixed inset-0 z-[100] bg-white dark:bg-gray-950 flex flex-col"
      style={{ height: `${visibleH}px` }}
    >
      {/* Off-screen single-column measurement for height estimation */}
      <div
        ref={measureRef}
        aria-hidden
        style={{
          position: 'fixed',
          left: '-200vw',
          top: 0,
          width: containerW,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div
          className="max-w-2xl mx-auto px-6 sm:px-12"
          style={{ fontFamily: fontFamilyCSS, padding: `${PAGE_PAD_Y}px 0` }}
        >
          <SlateRenderer
            content={activeContentNodes}
            fontSize={readingFontSize}
            fontFamily={slotFontFamily}
          />
        </div>
      </div>

      {/* ── Main reading area (full height, CSS column pagination) ── */}
      <div
        ref={containerRef}
        className="flex-1 min-h-0 relative overflow-hidden select-text"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => { handleTap(e); handleCenterTap(e) }}
      >
        {/* Loading overlay when switching branches */}
        {loadingBranch && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/80 dark:bg-gray-950/80 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
              Loading branch…
            </div>
          </div>
        )}
        {/* Left tap zone indicator */}
        {showControls && page > 0 && (
          <button
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/5 dark:bg-white/5 text-gray-400 hover:bg-black/10 dark:hover:bg-white/10 transition-colors hidden sm:block"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
        )}
        {/* Right tap zone indicator */}
        {showControls && page < totalPages - 1 && (
          <button
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full bg-black/5 dark:bg-white/5 text-gray-400 hover:bg-black/10 dark:hover:bg-white/10 transition-colors hidden sm:block"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        )}

        {/* All content rendered once; CSS columns split it into pages */}
        <div
          ref={columnRef}
          className={`${
            turning === 'next' ? 'animate-page-turn-next' :
            turning === 'prev' ? 'animate-page-turn-prev' : ''
          }`}
          style={{
            height: pageH,
            width: colContainerW,
            columnWidth: containerW,
            columnGap: COLUMN_GAP,
            columnFill: 'auto' as const,
            transform: `translateX(${-page * stride}px)`,
            padding: `${PAGE_PAD_Y}px 0`,
            overscrollBehavior: 'none',
          }}
        >
          <div
            className="max-w-2xl mx-auto px-6 sm:px-12"
            style={{
              fontFamily: fontFamilyCSS,
              WebkitBoxDecorationBreak: 'clone',
              boxDecorationBreak: 'clone' as const,
            }}
          >
            <SlateRenderer
              content={activeContentNodes}
              fontSize={readingFontSize}
              fontFamily={slotFontFamily}
              branchMarkers={activeBranchMarkers}
              onBranchClick={handleBranchClick}
            />
          </div>
          {/* Sentinel for measuring actual column extent */}
          <div ref={sentinelRef} aria-hidden style={{ height: 1, width: 1 }} />
        </div>
      </div>
      <div
        className={`absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-3 py-2 bg-white/95 dark:bg-gray-950/95 backdrop-blur border-b border-gray-200 dark:border-gray-800 transition-all duration-300 ${
          showControls ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-1">
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
            aria-label="Exit reading mode"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          {branchStack.length > 0 && (
            <button
              onClick={handleReturnToPrevious}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 text-xs font-medium transition-colors"
              title={`Return to "${branchStack[branchStack.length - 1].branchTitle}"`}
            >
              <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
              Return
            </button>
          )}
        </div>

        <div className="flex-1 min-w-0 text-center px-3">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{bookTitle}</p>
          {activeBranchTitle !== bookTitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{activeBranchTitle}</p>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => { setShowToc(t => !t); setShowSettings(false) }}
            className={`p-2 rounded-lg transition-colors ${showToc ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
            aria-label="Table of contents"
          >
            <ListBulletIcon className="w-5 h-5" />
          </button>
          <button
            onClick={toggleBookmark}
            className={`p-2 rounded-lg transition-colors ${
              isCurrentPageBookmarked
                ? 'text-primary-600 dark:text-primary-400'
                : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'
            }`}
            aria-label={isCurrentPageBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            {isCurrentPageBookmarked
              ? <BookmarkSolid className="w-5 h-5" />
              : <BookmarkIcon className="w-5 h-5" />}
          </button>
          <button
            onClick={() => { setShowSettings(s => !s); setShowToc(false) }}
            className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-600' : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
            aria-label="Reading settings"
          >
            <Cog6ToothIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ── Side panels ── */}
      {/* TOC panel */}
      {showToc && (
        <div className="absolute top-12 right-2 left-2 sm:left-auto sm:right-3 sm:w-72 z-50 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[60vh] overflow-y-auto">
          <div className="sticky top-0 bg-white dark:bg-gray-900 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100">Table of Contents</h3>
          </div>
          {chapterPageMap.length > 0 ? (
            <div className="py-2">
              {chapterPageMap.map(({ chapter, pageIdx }, i) => (
                <button
                  key={i}
                  onClick={() => goToPage(pageIdx)}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    pageIdx === page ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-700 dark:text-gray-300'
                  }`}
                  style={{ paddingLeft: `${(chapter.level - 1) * 12 + 16}px` }}
                >
                  {chapter.title}
                  <span className="text-xs text-gray-400 ml-2">p.{pageIdx + 1}</span>
                </button>
              ))}
            </div>
          ) : (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">No chapters detected</p>
          )}

          {/* Bookmarks section */}
          {bookmarks.length > 0 && (
            <>
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800">
                <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1">
                  <BookmarkSolid className="w-4 h-4 text-primary-500" /> Bookmarks
                </h3>
              </div>
              <div className="py-2">
                {bookmarks
                  .sort((a: { position: number }, b: { position: number }) => a.position - b.position)
                  .map((bm: { _id: string; position: number; note?: string }) => (
                    <div key={bm._id} className="flex items-center gap-1 px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <button
                        onClick={() => goToPage(bm.position)}
                        className={`flex-1 text-left text-sm ${bm.position === page ? 'text-primary-600 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                      >
                        Page {bm.position + 1}
                        {bm.note && <span className="text-xs text-gray-400 ml-1">– {bm.note}</span>}
                      </button>
                      <button
                        onClick={() => deleteBookmark({ bookmarkId: asId<"bookmarks">(bm._id) })}
                        className="p-1 rounded text-gray-400 hover:text-red-500 transition-colors"
                        aria-label="Delete bookmark"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Settings panel */}
      {showSettings && (
        <div className="absolute top-12 right-2 sm:right-3 z-50 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-[min(16rem,calc(100vw-1rem))] p-4">
          <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-3">Reading Settings</h3>
          {/* Theme */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">Theme</span>
            <div className="flex gap-1">
              {([['light', SunIcon, 'Light'], ['dark', MoonIcon, 'Dark'], ['auto', ComputerDesktopIcon, 'Auto']] as const).map(([t, Icon, label]) => (
                <button
                  key={t}
                  onClick={() => setTheme(t)}
                  className={`p-1.5 rounded transition-colors ${
                    theme === t
                      ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  aria-label={label}
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
          {/* Font size */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500 dark:text-gray-400">Font size</span>
            <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-600 rounded-lg px-1">
              <button
                onClick={() => fontSizeIdx > 0 && setReadingFontSize(FONT_SIZES[fontSizeIdx - 1])}
                disabled={fontSizeIdx === 0}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30"
              >
                <MinusIcon className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300 px-1 w-6 text-center select-none">
                {fontPx}
              </span>
              <button
                onClick={() => fontSizeIdx < FONT_SIZES.length - 1 && setReadingFontSize(FONT_SIZES[fontSizeIdx + 1])}
                disabled={fontSizeIdx === FONT_SIZES.length - 1}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-30"
              >
                <PlusIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          {/* Font family */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Font</span>
            <div className="flex gap-1">
              {(['serif', 'sans', 'mono'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setReadingFontFamily(f)}
                  className={`px-2 py-1 rounded text-xs transition-colors ${
                    readingFontFamily === f
                      ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 font-medium'
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  style={{ fontFamily: f === 'serif' ? 'Georgia, serif' : f === 'sans' ? 'system-ui, sans-serif' : 'monospace' }}
                >
                  {f === 'serif' ? 'Serif' : f === 'sans' ? 'Sans' : 'Mono'}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Bottom bar (overlaid) ── */}
      <div
        className={`absolute bottom-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-2 bg-white/95 dark:bg-gray-950/95 backdrop-blur border-t border-gray-200 dark:border-gray-800 transition-all duration-300 ${
          showControls ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          {page + 1} / {totalPages}
        </span>

        {/* Progress bar */}
        <div className="flex-1 mx-4 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 rounded-full transition-all duration-300"
            style={{ width: totalPages > 1 ? `${(page / (totalPages - 1)) * 100}%` : '100%' }}
          />
        </div>

        <span className="text-xs text-gray-500 dark:text-gray-400">
          {totalPages > 1 ? `${Math.round((page / (totalPages - 1)) * 100)}%` : '100%'}
        </span>
      </div>
    </div>
  )
}

export default ImmersiveReader
